<template>
  <PForm
    ref="pForm"
    :show-header="false"
    :primary-cta="formState.edit ? updateButton : {}"
    :secondary-cta="{}"
    body-class="q-px-2x-md"
    @submit.prevent="onSubmit"
  >
    <div class="col-12 q-mt-xxl">
      <span class="text-md text-weight-medium text-neutral">
        Payments Information
      </span>
      <q-separator class="q-mb-sm" />
    </div>
    <div class="row q-col-gutter-xl">
      <div class="col-6">
        <PSelectInput
          v-model="computedCreditCycle"
          :disable="formState.view || user?.basicInfo?.role === 'seller'"
          :options="unref(paymentTermsFilter?.entities)"
          :validations="{
            label: 'Credit Cycle',
          }"
          help="Select payments cycle from the dropdown"
          label="Credit Cycle (in days)"
          option-label="duration"
          option-value="duration"
          label-suffix="unit"
          placeholder="Select credit cycle"
          @filter="paymentTermsFilter?.onFilter"
          @filter-abort="paymentTermsFilter?.onFilterAbort"
        />
      </div>
      <div class="col-6">
        <PInput
          v-model.trim.number="computedCreditLimit"
          help="Leave as 1 if you don't
          have any info"
          :label="
            (user?.basicInfo?.role === 'seller' ? 'Advance' : 'Credit') +
            ' Limit'
          "
          min="1"
          placeholder="Enter Credit Limit"
          :validations="{
            type: 'integer',
            gte: 1,
          }"
          type="number"
        />
      </div>
    </div>
    <div v-if="user.application_status == 'approved'" class="q-mt-xxl">
      <div class="text-sm">Associated Agreements</div>
      <div class="text-neutral-70 text-xs">
        <span v-if="userSpecialDocuments?.length"
          >View all payments related agreement documents here</span
        >
        <span v-else
          >No agreement found!
          <span
            class="text-primary text-weight-medium cursor-pointer"
            @click="shiftToDocumentsTab"
          >
            Click here
          </span>

          to upload the document.</span
        >
      </div>
      <div
        v-for="(document, index) in userSpecialDocuments"
        :key="index"
        class="row items-center q-my-xs"
      >
        <DocumentIcon class="icon--sm text-primary" />
        <PBtn
          class="text-capitalize q-mx-xs"
          :class="
            document?.files?.length ? 'cursor-pointer' : 'cursor-not-allowed'
          "
          :label="document.name"
          flat
          dense
          rounded
          @click="
            () =>
              document?.files?.length
                ? viewFile({ url: document.files?.[0]?.url })
                : null
          "
        />
        <PBadge
          :label="getLabel(document.status_cd)"
          :type="
            document.status_cd == 'approved' ? 'signed' : document.status_cd
          "
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar } from "quasar";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import paymentsTermsForm from "../models/paymentsTermsForm";
import { onBeforeMount, unref } from "vue";
import DocumentIcon from "assets/icons/document.svg";

const { viewFile } = useFile();

const $q = useQuasar();

const pForm = ref(null);

const { formState, user } = storeToRefs(useUserStore());
const { putUser, toggleUserForm, getUserDocumentSignatureStatus } =
  useUserStore();
const { organisation } = storeToRefs(useOrganisationStore());

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const financeStepCompleted = inject("financeStepCompleted", () => false);

const mapPaymentTerms = (organisation) => {
  const userRole = unref(user)?.role;
  const orgId = unref(organisation)?._id;

  if (!unref(financeStepCompleted) && orgId) {
    const { credit_cycle, credit_limit, advance_limit } = unref(organisation);

    if (userRole === "seller") return { credit_limit: advance_limit };
    else if (userRole === "buyer")
      return {
        credit_cycle,
        credit_limit,
      };
  }
};

const computedFinanceStepCompleted = computed(() => {
  return unref(formState).edit || unref(financeStepCompleted);
});

const { formData, isLoading, submitForm } = useForm({
  formModel: paymentsTermsForm(mapPaymentTerms(organisation)),
  entity: unref(user).paymentsTermsInfo,
  formRef: pForm,
  isForm: formState,
  put: putUser,
  toggleForm: toggleUserForm,
  editMode: unref(computedFinanceStepCompleted),
});

const computedCreditLimit = computed({
  get: () => {
    return unref(formData).credit_limit;
  },
  set: (creditLimit) => {
    unref(formData).credit_limit = creditLimit || 1;
  },
});

const computedCreditCycle = computed({
  get: () => {
    const paymentTerms = unref(formData).credit_cycle;

    return (paymentTerms || "0") + (paymentTerms > 1 ? " days" : " day");
  },
  set: (paymentTerms) => {
    unref(formData).credit_cycle = paymentTerms ?? 0;
  },
});

const paymentTermsFilter = useFilter({
  entity: "paymentTerms",
});

const onSubmit = async () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to edit these details ?`,
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      closable: true,
      onConfirm: async () => {
        unref(formData).role = unref(user)?.basicInfo?.role;

        await submitForm(false);
      },
    },
  });
};

const emit = defineEmits(["update:modelValue"]);

const computedFormData = computed({
  get: function () {
    return props.modelValue;
  },
  set: function (updatedFormData) {
    emit("update:modelValue", updatedFormData);
  },
});

const updateButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Update",
    type: "update",
  };
});

watch(
  formData,
  (data) => {
    computedFormData.value = data;
  },
  {
    immediate: true,
  },
);

const userSpecialDocuments = ref(null);

const changeActiveTab = inject("updateActiveTab", () => {});

const shiftToDocumentsTab = () => {
  changeActiveTab("Documents");
};

const getLabel = (status) => {
  switch (status) {
    case "awaiting_response":
      return "Awaiting Response";

    case "approved":
      return "Signed";

    case "signed":
      return "Signed";

    case "partially_signed":
      return "Partially Signed";

    case "completed":
      return "Completed";

    case "rejected":
      return "Rejected";
  }
};

onBeforeMount(async () => {
  const documents = await getUserDocumentSignatureStatus();

  userSpecialDocuments.value = documents;
});
</script>
